// Translation strings - English
const enTranslations: Record<string, string> = {
  // Navigation
  'nav.home': 'Home',
  'nav.dashboard': 'DCF Analysis',
  'nav.tools': 'Market',
  'nav.about': 'About DCFlow',
  'nav.getStarted': 'Get Started',
  
  // Index Hero Section
  'hero.tag': 'Financial analysis, simplified',
  'hero.title': 'Build DCF models from your financial reports',
  'hero.description': 'Upload annual reports and financial statements. AI extracts the data, builds the DCF model, and gives you adjustable assumptions with clear visualizations — all in minutes.',
  'hero.getStarted': 'Start analyzing',
  'hero.learnMore': 'How it works',
  
  // Features Section
  'features.title': 'What you can do',
  'features.subtitle': 'From document to valuation',
  'features.description': 'Upload, extract, model, and visualize — without spending hours in spreadsheets',
  'features.ocr.title': 'Automatic data extraction',
  'features.ocr.description': 'Upload a PDF or image and AI identifies and extracts the financial data automatically',
  'features.ocr.feature1': 'PDF & image support',
  'features.ocr.feature2': 'Multi-language processing',
  'features.ocr.feature3': 'Validation & review',
  'features.dcf.title': 'Professional DCF modeling',
  'features.dcf.description': 'Discounted cash flow valuations with sensitivity analysis, WACC calculation, and custom scenarios',
  'features.dcf.feature1': 'Multi-year projections',
  'features.dcf.feature2': 'WACC calculation',
  'features.dcf.feature3': 'Terminal value analysis',
  'features.analytics.title': 'Clear, interactive charts',
  'features.analytics.description': 'Understand the numbers at a glance. Explore different scenarios and see how assumptions impact valuation',
  'features.analytics.feature1': 'Real-time charts',
  'features.analytics.feature2': 'Scenario comparison',
  'features.analytics.feature3': 'Sensitivity analysis',

  // Hero additional content
  'hero.aiPowered': 'AI-powered',
  'hero.financialIntelligence': 'Financial Analysis',
  'hero.additionalDescription': 'From document upload to valuation model — in minutes, not hours.',
  'hero.enterpriseSecurity': 'Secure processing',
  'hero.realTimeAnalysis': 'Real-time analysis',

  // Platform Capabilities
  'stats.accuracy.number': '3',
  'stats.accuracy.label': 'Core modules',
  'stats.accuracy.subtext': 'OCR · DCF · Market Data',
  'stats.documents.number': 'PDF & IMG',
  'stats.documents.label': 'Supported formats',
  'stats.documents.subtext': 'Drag & drop upload',
  'stats.companies.number': 'AI',
  'stats.companies.label': 'Data extraction',
  'stats.companies.subtext': 'Automatic financial data reading',
  'stats.time.number': 'Real-time',
  'stats.time.label': 'Market data',
  'stats.time.subtext': 'Live indices & stock quotes',

  // Secondary Features
  'secondary.speed.title': 'Fast processing',
  'secondary.speed.description': 'Upload and extract in seconds',
  'secondary.security.title': 'Secure by design',
  'secondary.security.description': 'Your data stays protected',
  'secondary.processing.title': 'Always available',
  'secondary.processing.description': 'Cloud-based infrastructure',
  'secondary.accuracy.title': 'Full transparency',
  'secondary.accuracy.description': 'Review and edit all extracted data',
  
  // How It Works Section
  'process.tag': 'How it works',
  'process.title': 'Three steps to a valuation',
  'process.description': 'Upload your financial documents, let AI extract the data, and get a DCF model ready to customize',
  'process.step1.title': 'Upload documents',
  'process.step1.description': 'Upload annual reports in PDF or image format. Multiple files supported for broader analysis.',
  'process.step2.title': 'Extract financial data',
  'process.step2.description': 'AI reads the documents, extracts key figures, and builds a preliminary DCF model automatically.',
  'process.step3.title': 'Adjust and analyze',
  'process.step3.description': 'Modify growth rates, discount rates, and other assumptions to see how they affect valuation in real time.',
  
  // CTA Section
  'cta.title': 'Start building your DCF model',
  'cta.description': 'Upload a financial report and get a complete valuation model in minutes. No manual data entry, no complex spreadsheets.',
  'cta.button': 'Start now',
  
  // Footer
  'footer.description': 'Financial analysis tool that extracts data from annual reports and builds dynamic DCF valuation models.',
  'footer.quickLinks': 'Quick Links',
  'footer.contact': 'Contact',
  'footer.email': 'Email',
  'footer.address': 'Address',
  'footer.privacy': 'Privacy Policy',
  'footer.terms': 'Terms of Service',
  'footer.cookies': 'Cookies',
  'footer.rights': 'All rights reserved.',
  'footer.location': 'Spain',
  'footer.emailUs': 'Email Us',
  'footer.emailAddress': 'Send email to insightechpro@gmail.com',
  
  // Language Selector
  'language': 'Language',
  'english': 'English',
  'spanish': 'Spanish',
  
  // Dashboard
  'dashboard.title': 'Financial Analysis',
  'dashboard.description': 'Upload financial documents, extract data, build DCF models, and visualize results',
  'dashboard.stockPrice': 'Stock price:',
  'dashboard.enterPrice': 'Enter price',
  'dashboard.analysisProgress': 'Analysis progress',
  'dashboard.previousStep': 'Previous step',
  'dashboard.nextStep': 'Next step',
  'dashboard.learnMore': 'Learn more about the platform',
  'dashboard.filesProcessedSuccess': 'Files processed successfully',
  'dashboard.filesProcessedDescription': 'Your documents have been analyzed. Proceed to extract financial data.',
  'dashboard.dataExtractedSuccess': 'Financial data extracted',
  'dashboard.dataExtractedDescription': 'Ready to build your DCF model with the extracted data.',
  'dashboard.modelCompleteSuccess': 'DCF model complete',
  'dashboard.modelCompleteDescription': 'Your valuation is ready to review.',
  'dashboard.cannotProceedError': 'Cannot proceed yet',
  'dashboard.uploadFilesFirst': 'Please upload and process files first',
  'dashboard.verifyDataFirst': 'Please verify and confirm the extracted data',
  'dashboard.completeDCFFirst': 'Please complete the DCF model calculations',
  'dashboard.actionRequired': 'Action required before proceeding',
  
  // About Page
  'about.title': 'About DCFlow',
  'about.nextGen.title': 'What is DCFlow?',
  'about.nextGen.description': 'DCFlow turns financial reports into ready-to-use valuation models. Upload a document, let AI extract the data, and in minutes you have a complete DCF model with charts, adjustable assumptions, and sensitivity analysis — no spreadsheets required.',
  'about.technology.title': 'Built for analysis',
  'about.technology.description': 'Interactive charts, real-time sensitivity analysis, and an interface designed so you can focus on what matters: understanding the numbers.',
  'about.technology.feature1': 'Automatic data extraction from PDFs and images',
  'about.technology.feature2': 'Professional-grade DCF modeling',
  'about.technology.feature3': 'Real-time sensitivity analysis',
  'about.commitment.title': 'What we believe in',
  'about.commitment.description': 'Company analysis should be accessible, accurate, and fast — without compromising the quality that serious analysis demands.',
  'about.commitment.feature1': 'Financial models continuously improving',
  'about.commitment.feature2': 'Works well on any device',
  'about.commitment.feature3': 'Accurate, up-to-date data',
  'about.howItWorks.title': 'How it works',
  'about.step1.title': 'Upload your document',
  'about.step1.description': 'Drag in an annual report, financial statement, or any document with accounting data.',
  'about.step2.title': 'Automatic extraction',
  'about.step2.description': 'AI reads your document and extracts the relevant figures: revenue, costs, cash flows, and more.',
  'about.step3.title': 'Your DCF model, ready',
  'about.step3.description': 'Adjust assumptions to your criteria and get a valuation with interactive charts instantly.',
  'about.cta.title': 'Start analyzing now',
  'about.cta.description': 'Upload your first financial document and generate a professional DCF model in minutes.',
  'about.cta.button': 'Go to analysis',

  // OCR
  'ocr.extractedData': 'Extracted Financial Data',
  'ocr.reviewEdit': 'Review and edit the extracted data before proceeding',
  'ocr.extractionNotices': 'Extraction notices',
  'ocr.rawText': 'Raw text',
  'ocr.editFinancialData': 'Edit Financial Data',
  'ocr.financialHighlights': 'Financial highlights',
  'ocr.consolidatedIncome': 'Income Statement',
  'ocr.cashFlows': 'Cash Flows',
  'ocr.balanceHighlights': 'Balance Sheet',
  'ocr.metricsOverview': 'Key Metrics',
  'ocr.dataEditor': 'Data editor',
  'ocr.dataQuality': 'Data quality',
  'ocr.dataQualityAnalysis': 'Data Quality Analysis',
  'ocr.dataQualityDescription': 'Review the quality and confidence scores of extracted data',
  'ocr.revenueData': 'Revenue',
  'ocr.operatingIncome': 'Operating income',
  'ocr.netIncome': 'Net income',
  'ocr.cashFlow': 'Cash flow',
  'ocr.highConfidence': 'High confidence',
  'ocr.mediumConfidence': 'Medium confidence',
  'ocr.lowConfidence': 'Low confidence',
  'ocr.extractedWithConfidence': 'Extracted with {{confidence}}% confidence from {{source}}',
  'ocr.overallDataQuality': 'Overall data quality',
  'ocr.dataQualityBasis': 'Based on clarity, consistency, and completeness',
  'ocr.verifyDataWarning': 'Important: Verify data before proceeding',
  'ocr.verifyDataDescription': 'Review all extracted financial data carefully. The accuracy of your DCF model depends on correct input values. Make corrections in the editor above.',
  'ocr.verifyDataBeforeDCF': 'Verify the data before building the DCF model',
  'ocr.confirmData': 'Confirm data',
  'ocr.extractedFinancialDataTitle': 'Extracted Financial Data',
  'ocr.extractedFinancialDataDesc': 'Financial data extracted from your documents',
  'ocr.metric': 'Metric',
  'ocr.revenue': 'Revenue',
  'ocr.operatingIncomeLabel': 'Operating income',
  'ocr.netIncomeLabel': 'Net income',
  'ocr.cashFlowLabel': 'Cash flow',
  'ocr.assetsLabel': 'Assets',
  'ocr.liabilitiesLabel': 'Liabilities',
  'ocr.noDataAvailable': 'No financial data available. Check the extracted information.',
  'ocr.verifyMetricsWarning': 'Verify that extracted metrics are correct',
  'ocr.verifyMetricsDescription': 'Incorrect data will affect your valuation results.',
  'ocr.incomeStatement': 'Income Statement',
  'ocr.balanceSheet': 'Balance Sheet',
  'ocr.notes': 'Notes',
  
  // AI Configuration
  'ai.configureAPI': 'Configure your AI API for document processing',
  'ai.settingsTitle': 'AI API Configuration',
  'ai.configurationDescription': 'Configure AI APIs for document processing and financial data extraction. Keys are stored locally in your browser.',
  'ai.securityNotice': 'API keys are stored in session storage with obfuscation. Keys are cleared when you close the browser.',
  'ai.documentProcessing': 'Document Processing (OCR)',
  'ai.dataAnalysis': 'Financial Data Analysis (NLP)',
  'ai.provider': 'Provider',
  'ai.apiKey': 'API Key',
  'ai.testConnection': 'Test Connection',
  'ai.testing': 'Testing...',
  'ai.saveConfiguration': 'Save Configuration',
  'ai.saving': 'Saving...',
  'ai.delete': 'Delete',
  'ai.connectionSuccess': 'Connection successful',
  'ai.connectionError': 'Connection error',
  'ai.testingConnection': 'Testing connection...',
  'ai.configurationSaved': 'Configuration saved',
  'ai.configurationSavedDescription': 'API configurations saved and verified.',
  'ai.configurationDeleted': 'Configuration deleted',
  'ai.configurationDeletedDescription': 'All API configurations have been deleted.',
  'ai.apiKeyRequired': 'API key required',
  'ai.apiKeyRequiredDescription': 'Enter an API key before testing the connection.',
  'ai.connectionSuccessful': 'Connection successful',
  'ai.connectionSuccessfulDescription': 'The API key works correctly.',
  'ai.troubleshooting': '💡 Troubleshooting:',
  'ai.verifyKey': 'Verify that your API key is correct and valid',
  'ai.openAIFormat': 'OpenAI: Keys must start with "sk-"',
  'ai.anthropicFormat': 'Anthropic: Keys must start with "sk-ant-"',
  'ai.checkCredits': 'Make sure you have available credits in your account',
  'ai.checkPermissions': 'Check that your API key has the necessary permissions',
  'ai.autoModelSelection': '🤖 Automatic model selection:',
  'ai.autoModelDescription': 'The system detects available models and selects the most suitable one for each task.',
  
  // AI Validation messages
  'ai.validation.apiKeyRequired': 'API key is required',
  'ai.validation.openAIFormat': 'OpenAI API keys must start with "sk-"',
  'ai.validation.anthropicFormat': 'Anthropic API keys must start with "sk-ant-"',
  'ai.validation.keyTooShort': 'API key seems too short',
  'ai.validation.keyCleaned': 'API key cleaned',
  'ai.validation.keyCleanedDescription': 'Special characters were removed from the API key.',
  'ai.validation.unknownError': 'Unknown error verifying connection.',
  'ai.validation.configurationError': 'Configuration error',

  // Document QA
  'docqa.title': 'Document Q&A',
  'docqa.description': 'Upload a financial document and ask questions about its content.',
  'docqa.uploadDocument': 'Upload Document',
  'docqa.askQuestions': 'Ask Questions',
  'docqa.fullyLocalNote': 'All document processing happens securely in your browser.',

  // Upload and File Processing
  'upload.title': 'Upload Financial Documents',
  'upload.description': 'Upload annual reports or financial statements',
  'upload.dropFiles': 'Drop files here or click to select',
  'upload.supportedFormats': 'Supported formats: PDF, PNG, JPG, JPEG',
  'upload.maxFileSize': 'Max file size: 10 MB',
  'upload.aiProcessing': 'Enable AI Processing',
  'upload.aiDescription': 'Use AI to automatically extract financial data from your documents',
  'upload.processFiles': 'Process files',
  'upload.processing': 'Processing...',
  'upload.processingWithAI': 'Processing with AI...',
  'upload.processWithAI': 'Process with AI',
  'upload.invalidFileType': 'Invalid file type',
  'upload.invalidFileTypeDescription': 'Only PDF and image files are supported.',
  'upload.noFilesSelected': 'No files selected',
  'upload.uploadAtLeastOneFile': 'Upload at least one file to process.',
  'upload.aiProcessingCompleted': 'AI processing completed',
  'upload.aiProcessingCompletedDesc': '{{count}} file(s) processed with AI.',
  'upload.aiNotConfigured': 'AI not configured',
  'upload.aiNotConfiguredDescription': 'Configure your API key before using AI processing.',
  'upload.browseFiles': 'Browse files',
  'upload.or': 'or',
  'upload.securityNote': 'Files are processed securely.',
  'upload.aiEnabled': 'AI enabled',
  'upload.enableAI': 'Enable AI',
  'upload.processingFailed': 'Processing failed',
  'upload.processingFailedDescription': 'An error occurred while processing the files.',

  // DCF Model
  'dcf.title': 'DCF Model',
  'dcf.description': 'Configure your Discounted Cash Flow model assumptions',
  'dcf.basicAssumptions': 'Basic Assumptions',
  'dcf.advancedAssumptions': 'Advanced Assumptions',
  'dcf.projections': 'Projections',
  'dcf.valuation': 'Valuation',

  // Visualization
  'viz.title': 'Financial Visualizations',
  'viz.description': 'Explore your financial data through interactive charts',

  // Navigation Steps
  'tabs.upload': 'Upload',
  'tabs.ocr': 'Extract',
  'tabs.dcf': 'Model',
  'tabs.visualization': 'Results',
  'tabs.docqa': 'Q&A',
  'tabs.ocrFull': 'Extracted Data',
  'tabs.dcfFull': 'DCF Model',
  'tabs.vizFull': 'Visualizations',
  'tabs.docqaFull': 'Document Q&A',
  'tabs.tooltip.ocr': 'Upload a document first to unlock this step',
  'tabs.tooltip.dcf': 'Extract or enter financial data first to unlock this step',
  'tabs.tooltip.visualization': 'Complete the DCF model first to unlock this step',

  // AI Assistant
  'ai.title': 'AI Financial Assistant',
  'ai.description': 'Get insights and recommendations based on your financial analysis',
  'ai.askQuestion': 'Ask me about your financial analysis',
  'ai.helpText': 'I can help with investment decisions, risk assessment, and valuation insights.',
  'ai.placeholder': 'Ask about investment risks, valuation, or recommendations...',
  'ai.send': 'Send',
  'ai.processing': 'Processing...',
  'ai.error': 'Failed to get a response. Please try again.',
  'ai.suggestions.investment': 'What is your investment recommendation based on this DCF analysis?',
  'ai.suggestions.risks': 'What are the key risks to consider for this investment?',
  'ai.suggestions.multiples': 'How does this valuation compare to typical market multiples?',
  'ai.suggestions.factors': 'What factors could significantly impact this company\'s valuation?',

  // Tools Page
  'tools.title': 'Financial Tools',
  'tools.description': 'Financial calculators and real-time market data',
  'tools.calculators': 'Financial Calculators',
  'tools.market': 'Market Data',
  'tools.calculatorsDesc': 'DCF, Compound Interest, Present Value, IRR, ROI, CAGR, and Breakeven calculators.',
  'tools.marketDesc': 'Real-time indices, stock search, and personal watchlist.',
  'tools.backToTools': 'Back to tools',
  'tools.openCalculators': 'Open calculators',
  'tools.openMarket': 'Open market data',
  'tools.fullAnalysis': 'Full analysis available',
  'tools.fullAnalysisDesc': 'For complete DCF modeling with document upload and AI extraction, go to the dashboard.',
  'tools.goToDashboard': 'Go to dashboard',

  // Market Data
  'market.title': 'Market Data',
  'market.description': 'Real-time market data and key economic indicators',
  'market.indicators.title': 'Market Indicators',
  'market.indicators.description': 'Major indices and economic indicators',
  'market.stocks.title': 'Top Stocks',
  'market.stocks.description': 'Stock prices and key metrics for major companies',
  'market.status.title': 'Market Status',
  'market.status.open': 'Market open',
  'market.status.hours': 'NYSE: 9:30 AM – 4:00 PM EST',
  'market.gainers.title': 'Top gainers',
  'market.losers.title': 'Top losers',
  'market.stock.change': 'Change:',
  'market.stock.volume': 'Volume:',
  'market.stock.marketCap': 'Market cap:',
  'market.stock.peRatio': 'P/E ratio:',
  'market.stock.divYield': 'Div. yield:',

  // Financial Calculators
  'calc.title': 'Financial Calculators',
  'calc.description': 'Solve investment and valuation problems with professional calculators',
  'calc.dcf': 'DCF Calculator',
  'calc.compound': 'Compound Interest',
  'calc.presentValue': 'Present Value',
  'calc.irr': 'IRR Calculator',

  // DCF Calculator
  'calc.dcf.title': 'Quick DCF Valuation',
  'calc.dcf.description': 'Estimate intrinsic value using discounted cash flow methodology',
  'calc.dcf.currentFCF': 'Current Free Cash Flow',
  'calc.dcf.sharesOutstanding': 'Shares Outstanding',
  'calc.dcf.growthRate': 'Growth Rate (%)',
  'calc.dcf.terminalGrowth': 'Terminal Growth Rate (%)',
  'calc.dcf.discountRate': 'Discount Rate (%)',
  'calc.dcf.calculate': 'Calculate fair value',
  'calc.dcf.fairValue': 'Fair value:',
  'calc.dcf.perShare': 'per share',
  'calc.dcf.enterpriseValue': 'Enterprise value:',
  'calc.dcf.pvProjectedFCFs': 'PV of projected FCFs:',
  'calc.dcf.pvTerminalValue': 'PV of terminal value:',
  'calc.dcf.terminalValueMultiple': 'Terminal value multiple:',
  'calc.dcf.impliedPERatio': 'Implied P/E ratio:',

  // Compound Interest Calculator
  'calc.compound.title': 'Compound Interest',
  'calc.compound.description': 'Calculate the effect of compound interest over time',
  'calc.compound.principal': 'Principal Amount',
  'calc.compound.rate': 'Annual Interest Rate (%)',
  'calc.compound.time': 'Time Period (years)',
  'calc.compound.frequency': 'Compounding Frequency (per year)',
  'calc.compound.calculate': 'Calculate future value',
  'calc.compound.futureValue': 'Future value:',
  'calc.compound.principalResult': 'Principal:',
  'calc.compound.totalInterest': 'Total interest earned:',
  'calc.compound.effectiveRate': 'Effective annual rate:',
  'calc.compound.totalReturn': 'Total return:',
  'calc.compound.averageGrowth': 'Average annual growth:',

  // Present Value Calculator
  'calc.pv.title': 'Present Value',
  'calc.pv.description': 'Calculate the present value of future cash flows',
  'calc.pv.futureValue': 'Future Value',
  'calc.pv.discountRate': 'Discount Rate (%)',
  'calc.pv.timePeriod': 'Time Period (years)',
  'calc.pv.calculate': 'Calculate present value',
  'calc.pv.presentValue': 'Present value:',
  'calc.pv.futureValueResult': 'Future value:',
  'calc.pv.totalDiscount': 'Total discount:',
  'calc.pv.annualDiscountRate': 'Annual discount rate:',
  'calc.pv.annualDiscountFactor': 'Annual discount factor:',
  'calc.pv.totalDiscountFactor': 'Total discount factor:',

  // IRR Calculator
  'calc.irr.title': 'IRR Calculator',
  'calc.irr.description': 'Calculate Internal Rate of Return for investment projects',
  'calc.irr.initialInvestment': 'Initial Investment',
  'calc.irr.cashFlows': 'Annual Cash Flows (comma-separated)',
  'calc.irr.calculate': 'Calculate IRR',
  'calc.irr.initialInvestmentResult': 'Initial investment:',
  'calc.irr.totalCashReturns': 'Total cash returns:',
  'calc.irr.netProfit': 'Net profit:',
  'calc.irr.npvAt10': 'NPV (at 10% discount):',
  'calc.irr.paybackPeriod': 'Payback period:',
  'calc.irr.notAchieved': 'Not achieved in projection',
  'calc.irr.years': 'years',

  // ROI Calculator
  'calc.roi': 'ROI',
  'calc.roi.title': 'ROI Calculator',
  'calc.roi.description': 'Calculate Return on Investment for any project or asset',
  'calc.roi.cost': 'Investment Cost',
  'calc.roi.returns': 'Total Returns',
  'calc.roi.calculate': 'Calculate ROI',
  'calc.roi.costResult': 'Investment cost',
  'calc.roi.returnsResult': 'Total returns',
  'calc.roi.profit': 'Net profit/loss',
  'calc.roi.multiple': 'Return multiple',

  // CAGR Calculator
  'calc.cagr': 'CAGR',
  'calc.cagr.title': 'CAGR Calculator',
  'calc.cagr.description': 'Calculate Compound Annual Growth Rate between two values',
  'calc.cagr.beginLabel': 'Beginning Value',
  'calc.cagr.endLabel': 'Ending Value',
  'calc.cagr.periodLabel': 'Time Period',
  'calc.cagr.calculate': 'Calculate CAGR',
  'calc.cagr.beginValue': 'Beginning value',
  'calc.cagr.endValue': 'Ending value',
  'calc.cagr.totalReturn': 'Total return',
  'calc.cagr.years': 'Period',
  'calc.cagr.multiple': 'Growth multiple',

  // Breakeven Calculator
  'calc.breakeven': 'Breakeven',
  'calc.breakeven.title': 'Breakeven Calculator',
  'calc.breakeven.description': 'Find the units needed to cover all costs',
  'calc.breakeven.fixedLabel': 'Fixed Costs',
  'calc.breakeven.priceLabel': 'Price per Unit',
  'calc.breakeven.variableLabel': 'Variable Cost/Unit',
  'calc.breakeven.calculate': 'Calculate breakeven',
  'calc.breakeven.pointLabel': 'Breakeven point',
  'calc.breakeven.fixedCosts': 'Fixed costs',
  'calc.breakeven.priceUnit': 'Price per unit',
  'calc.breakeven.variableUnit': 'Variable cost/unit',
  'calc.breakeven.contributionMargin': 'Contribution margin',
  'calc.breakeven.revenue': 'Breakeven revenue',
  'calc.breakeven.unitsLabel': 'units',

  // Calculator tooltips
  'calc.tip.fcf': 'Operating cash flow minus capital expenditures',
  'calc.tip.shares': 'Total diluted shares outstanding',
  'calc.tip.growth': 'Expected annual revenue growth rate',
  'calc.tip.terminalGrowth': 'Long-term sustainable growth rate (typically 2–3%)',
  'calc.tip.wacc': 'Weighted average cost of capital (WACC)',
  'calc.tip.principal': 'Starting investment amount',
  'calc.tip.futureValue': 'The amount you expect to receive in the future',
  'calc.tip.initialInv': 'Total amount invested upfront',
  'calc.tip.cashFlows': 'Expected yearly returns, comma-separated',
  'calc.validation.discountRate': 'Discount rate must exceed terminal growth rate',
  'calc.validation.priceAboveVariable': 'Price must exceed variable cost per unit',

  // Comparison mode
  'calc.compare.toggle': 'Compare mode',
  'calc.compare.saveScenario': 'Save scenario',
  'calc.compare.scenarioName': 'Scenario name',
  'calc.compare.savedScenarios': 'Saved scenarios',
  'calc.compare.noScenarios': 'Calculate and save scenarios to compare them side by side',
  'calc.compare.clear': 'Clear all',
  'calc.compare.remove': 'Remove',
  'calc.compare.vsBaseline': 'vs baseline',
  'calc.compare.result': 'Result',
  'calc.compare.exportCSV': 'Export CSV',
  'calc.compare.exportPDF': 'Export PDF',
  'calc.compare.export': 'Export',
  'calc.compare.metric': 'Metric',
  'calc.compare.scenarioComparison': 'Scenario Comparison',
  'calc.compare.generatedOn': 'Generated on',
  'calc.compare.difference': 'Difference',

  'chatbot.title': 'Financial Assistant',
  'chatbot.poweredBy': 'AI-powered',
  'chatbot.thinking': 'Thinking...',
  'chatbot.placeholder': 'Ask a financial question...',
  'chatbot.welcome.title': 'Financial Assistant',
  'chatbot.welcome.description': 'Ask about investing, valuation, markets, accounting, or any financial topic.',
  'chatbot.welcome.q1': 'What is a DCF valuation?',
  'chatbot.welcome.q2': 'Explain the P/E ratio',
  'chatbot.welcome.q3': 'How does WACC work?',
  'chatbot.welcome.q4': 'EBITDA vs Net Income?',
  'chatbot.clear': 'Clear conversation',
  'chatbot.open': 'Open financial assistant',

  // Dashboard steps
  'dashboard.step.upload': 'Upload',
  'dashboard.step.extraction': 'Extract data',
  'dashboard.step.dcf': 'DCF Model',
  'dashboard.step.visualization': 'Results',
  'dashboard.step.uploadDesc': 'Upload your financial reports (PDF or images) to start the analysis.',
  'dashboard.step.extractionDesc': 'Review and verify extracted financial data, or enter data manually.',
  'dashboard.step.dcfDesc': 'Configure assumptions and run the Discounted Cash Flow model.',
  'dashboard.step.visualizationDesc': 'Explore interactive charts, sensitivity analysis, and valuation results.',
  'dashboard.keyboardHint': 'Tip: Use ← → arrow keys to navigate between steps',

  // Clear Data
  'dashboard.clearData.button': 'Clear Data',
  'dashboard.clearData.title': 'Clear all data?',
  'dashboard.clearData.description': 'This will remove all uploaded files, extracted data, financial information, and DCF results. This action cannot be undone.',
  'dashboard.clearData.cancel': 'Cancel',
  'dashboard.clearData.confirm': 'Clear All',
  'dashboard.clearData.success': 'All data cleared',
  'dashboard.clearData.successDesc': 'Ready to start a new analysis',

  // Quick Stats
  'quickStats.revenue': 'Revenue',
  'quickStats.netIncome': 'Net Income',
  'quickStats.freeCashFlow': 'Free Cash Flow',
  'quickStats.impliedPrice': 'Implied Price',
  'quickStats.noData': 'Upload or enter financial data to see key metrics here.',

  // Financial Constellation Nodes
  'node.revenue.label': 'Revenue',
  'node.revenue.description': 'Total income from sales before expenses',
  'node.revenue.details': 'Revenue is the starting point of any DCF model — total income from selling goods or services. Historical revenue trends drive growth rate assumptions that shape all projected cash flows.\n\nFormula:\nRevenue = Price × Units Sold\nGrowth Rate = (Revenue₁ − Revenue₀) / Revenue₀',

  'node.ebitda.label': 'EBITDA',
  'node.ebitda.description': 'Earnings before interest, taxes, D&A',
  'node.ebitda.details': 'EBITDA measures operating profitability by stripping out financing and accounting decisions. It isolates core business performance and is widely used for cross-company comparisons.\n\nFormula:\nEBITDA = Revenue − COGS − Operating Expenses + D&A\nor equivalently:\nEBITDA = Net Income + Interest + Taxes + D&A',

  'node.capex.label': 'CapEx',
  'node.capex.description': 'Capital expenditures for long-term assets',
  'node.capex.details': 'Capital Expenditures are investments in property, plant, and equipment needed to maintain or expand operations. CapEx is subtracted when computing Free Cash Flow because it represents cash already committed.\n\nFormula:\nCapEx = PP&E (end) − PP&E (start) + Depreciation',

  'node.fcf.label': 'FCF',
  'node.fcf.description': 'Free Cash Flow available to investors',
  'node.fcf.details': 'Free Cash Flow is the cash a business generates after covering operating costs and capital investments. It represents the actual cash available to pay dividends, reduce debt, or reinvest.\n\nFormula:\nFCF = EBIT × (1 − Tax Rate) + D&A − CapEx − ΔWorking Capital',

  'node.discount.label': 'Discount Rate',
  'node.discount.description': 'Rate to discount future cash flows',
  'node.discount.details': 'The discount rate accounts for the time value of money and investment risk. A higher rate reduces the present value of future cash flows, reflecting greater uncertainty.\n\nIn DCF models the discount rate is typically set equal to the WACC.\n\nFormula:\nPV = FCFₜ / (1 + r)ᵗ\nwhere r = discount rate, t = year',

  'node.wacc.label': 'WACC',
  'node.wacc.description': 'Weighted Average Cost of Capital',
  'node.wacc.details': 'WACC blends the cost of equity (CAPM) and the after-tax cost of debt, weighted by their proportions in the capital structure. It sets the minimum return a company must earn to satisfy all capital providers.\n\nFormula:\nWACC = (E/V) × Ke + (D/V) × Kd × (1 − T)\nwhere Ke = cost of equity, Kd = cost of debt, T = tax rate',

  'node.terminal.label': 'Terminal Value',
  'node.terminal.description': 'Value beyond the forecast period',
  'node.terminal.details': 'Terminal Value captures a company\'s worth beyond the explicit forecast horizon. It typically accounts for 60–80% of total enterprise value, making growth and discount assumptions critical.\n\nGordon Growth Model:\nTV = FCFₙ × (1 + g) / (WACC − g)\n\nExit Multiple Method:\nTV = EBITDAₙ × Exit Multiple',

  'node.npv.label': 'NPV',
  'node.npv.description': 'Net Present Value of all cash flows',
  'node.npv.details': 'Net Present Value sums all discounted future cash flows and subtracts the initial investment. A positive NPV indicates the investment creates value; a negative NPV destroys it.\n\nFormula:\nNPV = Σ [FCFₜ / (1 + r)ᵗ] + TV / (1 + r)ⁿ',
};

export default enTranslations;
